import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type { InternalAuditReportTestResultsByControlIdResponse } from '@risksmart-app/trpc/src/types';
import type { GetInternalAuditReportTestResultsByControlIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

/**
 * Maps TRPC test results data to match the GraphQL query structure
 */
const mapTrpcTestResultsToGraphQL = (
  trpcData: InternalAuditReportTestResultsByControlIdResponse
): GetInternalAuditReportTestResultsByControlIdQuery => {
  return {
    control_test_internal_audit_result:
      trpcData.control_test_internal_audit_result,
  };
};

export const useGetInternalAuditReportTestResultsByControlId = (
  controlId: string,
  skip?: boolean
) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.testResult.internalAuditReportTestResultsByControlId.queryOptions(
      {
        controlId: controlId,
      }
    ),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = trpcData ? mapTrpcTestResultsToGraphQL(trpcData) : undefined;

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
    error: trpcError,
  };
};
